import {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  useMemo,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { AUTH_DISABLED } from 'src/App/Providers';
import { MatchedByType, PredictionOpportunity, WSPredictionResponse } from '../types';
import { predictionsReducer, initialState } from './predictionsReducer';

interface PredictionsContextProps {
  opportunities: PredictionOpportunity[];
  isConnected: boolean;
  count: number;
  removePair: (pairId: string) => void;
  confirmPair: (pairId: string, matchedBy: MatchedByType) => void;
}

const PredictionsContext = createContext<PredictionsContextProps | undefined>(
  undefined
);

export const PredictionsProvider = ({ children }: { children: ReactNode }) => {
  const accessToken = _getToken();

  const [state, dispatch] = useReducer(predictionsReducer, initialState);

  const opportunities = useMemo(
    () => Array.from(state.cache.values()),
    [state.cache]
  );

  const removePair = useCallback((pairId: string) => {
    dispatch({ type: 'REMOVE_PAIR', pairId });
  }, []);

  const confirmPair = useCallback((pairId: string, matchedBy: MatchedByType) => {
    dispatch({ type: 'CONFIRM_PAIR', pairId, matchedBy });
  }, []);

  const { socket } = useSocket(
    import.meta.env.VITE_PREDICTIONS_WS_URL + '/predictions',
    {
      enabled: AUTH_DISABLED || !!accessToken,
      auth: { token: accessToken },
    }
  );

  // Listen for prediction events
  const { lastMessage: snapshotMessage } = useSocketEvent<WSPredictionResponse>(
    socket,
    'prediction_snapshot'
  );

  const { lastMessage: updateMessage } = useSocketEvent<WSPredictionResponse>(
    socket,
    'prediction_update'
  );

  const hasSubscribed = useRef(false);

  // Handle connection — subscribe on connect
  useEffect(() => {
    if (!socket.connected) {
      hasSubscribed.current = false;
      return;
    }

    if (!hasSubscribed.current) {
      socket.emit('subscribe_predictions');
      hasSubscribed.current = true;
    }
  }, [socket.connected]);

  // Handle snapshot messages
  useEffect(() => {
    if (snapshotMessage?.type === 'SNAPSHOT') {
      dispatch(snapshotMessage);
    }
  }, [snapshotMessage]);

  // Handle update messages
  useEffect(() => {
    if (updateMessage?.type === 'UPDATE') {
      dispatch(updateMessage);
    }
  }, [updateMessage]);

  // Periodic cleanup — remove markets not updated in 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: 'CLEANUP' });
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const isConnected = socket.connected;

  return (
    <PredictionsContext.Provider
      value={{
        opportunities,
        isConnected,
        count: opportunities.length,
        removePair,
        confirmPair
      }}
    >
      {children}
    </PredictionsContext.Provider>
  );
};

export const usePredictions = () => {
  const context = useContext(PredictionsContext);
  if (!context) {
    throw new Error('usePredictions must be used within a PredictionsProvider');
  }
  return context;
};
